import { useCallback, useEffect, useState } from "react";
import { useApp } from "../../store.jsx";
import { useRole } from "../../hooks/useRole";
import {
  getProject,
  getProjectsMeta,
  updateProject,
  unlinkProjectTask,
} from "../../lib/api";
import { Icon } from "../../components/Common.jsx";
import PageBoundary from "../../components/PageBoundary.jsx";
import ProjectFormModal from "../../components/projects/ProjectFormModal.jsx";
import ProgressBar from "../../components/projects/ProgressBar.jsx";
import LinkTasksModal from "../../components/projects/LinkTasksModal.jsx";
import { AssignTaskModal } from "../../components/Modals.jsx";

const fmtD = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const barColor = (pct) =>
  pct >= 80 ? "var(--success)" : pct >= 40 ? "var(--warning)" : "var(--error)";

function ProjectDetailInner() {
  const { pageContext, goPage, openModal, closeModal, toast } = useApp();
  const role = useRole();
  const canManage = role === "owner" || role === "manager";
  const projectId = pageContext?.projectId;

  const [project, setProject] = useState(null);
  const [meta, setMeta] = useState({ statuses: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { project: p } = await getProject(projectId);
      setProject(p);
    } catch (e) {
      toast(e.message || "Could not load project", "error");
      goPage("projects");
    } finally {
      setLoading(false);
    }
  }, [projectId, goPage, toast]);

  useEffect(() => {
    if (!projectId) {
      goPage("projects"); // e.g. after a refresh wiped pageContext
      return;
    }
    getProjectsMeta()
      .then(setMeta)
      .catch(() => {});
    load();
  }, [projectId, load, goPage]);

  if (!projectId) return null;
  if (loading || !project) {
    return (
      <section>
        <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
          Loading…
        </div>
      </section>
    );
  }

  const archived = project.status === "archived";
  const st =
    (meta.statuses || []).find((s) => s.value === project.status) || {
      label: project.status,
      badge: "badge-muted",
    };
  const pct = project.progressPct || 0;

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await updateProject(project.id, { status });
      toast(`Project ${status}`, "success");
      await load();
    } catch (e) {
      toast(e.message || "Could not update project", "error");
    } finally {
      setBusy(false);
    }
  };

  const doUnlink = async (taskId) => {
    setBusy(true);
    try {
      await unlinkProjectTask(project.id, taskId);
      await load();
    } catch (e) {
      toast(e.message || "Could not unlink task", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => goPage("projects")}
        style={{ marginBottom: 12 }}
      >
        <Icon name="chevron-left" size={14} /> Back to Projects
      </button>

      <div className="page-head">
        <div>
          <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {project.name}
            <span className={`badge ${st.badge}`}>{st.label}</span>
            {project.overdue && <span className="badge badge-error">Overdue</span>}
          </h1>
          <p className="page-sub">
            {project.deadline ? `Due ${fmtD(project.deadline)}` : "No deadline"} · {project.taskCompleted}/
            {project.taskTotal} tasks done
            {project.createdBy ? ` · by ${project.createdBy.name}` : ""}
          </p>
        </div>
        {canManage && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className="btn btn-ghost btn-sm"
              disabled={archived || busy}
              onClick={() => openModal(<ProjectFormModal project={project} onSaved={load} />, "lg")}
            >
              <Icon name="edit-2" size={14} /> Edit
            </button>
            {project.status === "active" && (
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setStatus("completed")}>
                <Icon name="check" size={14} /> Complete
              </button>
            )}
            {project.status === "completed" && (
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setStatus("active")}>
                <Icon name="rotate-ccw" size={14} /> Reopen
              </button>
            )}
            {!archived && (
              <button
                className="btn btn-ghost btn-sm"
                disabled={busy}
                onClick={() => {
                  if (window.confirm("Archive this project? It becomes read-only.")) setStatus("archived");
                }}
              >
                <Icon name="archive" size={14} /> Archive
              </button>
            )}
          </div>
        )}
      </div>

      {archived && (
        <div
          className="card"
          style={{ padding: "10px 14px", marginBottom: 14, color: "var(--text-muted)", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}
        >
          <Icon name="lock" size={14} /> This project is archived — read only.
        </div>
      )}

      <div className="card" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
          <div className="card-title">Progress</div>
          <div style={{ fontWeight: 800, fontSize: 20, color: barColor(pct), fontVariantNumeric: "tabular-nums" }}>
            {pct}%
          </div>
        </div>
        <ProgressBar pct={pct} height={10} />
        {project.description && (
          <p style={{ marginTop: 12, color: "var(--text-muted)", fontSize: 13 }}>{project.description}</p>
        )}
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <div className="card-title">Members ({project.members?.length || 0})</div>
            {canManage && !archived && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => openModal(<ProjectFormModal project={project} onSaved={load} />, "lg")}
              >
                Manage
              </button>
            )}
          </div>
          {project.members?.length ? (
            project.members.map((m) => (
              <div key={m.id} className="activity-row">
                <div className="activity-text">
                  <div className="activity-title" style={{ fontWeight: 600 }}>{m.user?.name}</div>
                  <div className="activity-meta">
                    {(m.user?.department || "—") + " · " + (m.user?.role || "")}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={{ color: "var(--text-muted)", fontSize: 13, padding: 12 }}>No members yet.</div>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">Linked tasks ({project.tasks?.length || 0})</div>
            {canManage && !archived && (
              <>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => openModal(<LinkTasksModal projectId={project.id} onSaved={load} />, "md")}
                >
                  <Icon name="link" size={14} /> Link tasks
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() =>
                    openModal(
                      <AssignTaskModal
                        projectId={project.id}
                        onClose={closeModal}
                        onSubmit={() => {
                          toast("Task created and linked", "success");
                          load();
                        }}
                      />,
                      "md",
                    )
                  }
                >
                  <Icon name="plus" size={14} /> New task
                </button>
              </>
            )}
          </div>
          {project.tasks?.length ? (
            project.tasks.map((pt) => (
              <div key={pt.id} className="activity-row is-wrap">
                <Icon
                  name={pt.task?.completedAt ? "check-circle" : "circle"}
                  size={16}
                  style={{ color: pt.task?.completedAt ? "var(--success)" : "var(--text-muted)", flexShrink: 0, marginTop: 2 }}
                />
                <div className="activity-text">
                  <div className="activity-title" style={{ fontWeight: 600 }} title={pt.task?.title}>{pt.task?.title}</div>
                  <div className="activity-meta">
                    {(pt.task?.user?.name || "—") + " · " + (pt.task?.completedAt ? "done" : pt.task?.status)}
                  </div>
                </div>
                {canManage && !archived && (
                  <button className="icon-btn" title="Unlink" disabled={busy} onClick={() => doUnlink(pt.taskId)}>
                    <Icon name="x" size={14} />
                  </button>
                )}
              </div>
            ))
          ) : (
            <div style={{ color: "var(--text-muted)", fontSize: 13, padding: 12 }}>
              No tasks linked.{canManage ? " Link active tasks to track progress from daily reports." : ""}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function ProjectDetail() {
  return (
    <PageBoundary>
      <ProjectDetailInner />
    </PageBoundary>
  );
}
